import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get_driver_detail } from "../../utils/api";
import {
  Avatar,
  Badge,
  Box,
  Card,
  Divider,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Skeleton,
  SkeletonCircle,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import ImageModal from "../../components/ImageModal";
import {
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiShield,
  FiFileText,
  FiCreditCard,
  FiHash,
} from "react-icons/fi";

// ── Section Header ─────────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, color = "blue.500", count }) => (
  <Flex align="center" gap={3} mb={4}>
    <Box
      w="34px"
      h="34px"
      borderRadius="9px"
      bg={`${color.split(".")[0]}.50`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={icon} color={color} boxSize={4} />
    </Box>
    <Flex align="center" gap={2}>
      <Text fontWeight="700" fontSize="md" color="gray.700">
        {title}
      </Text>
      {count != null && (
        <Badge colorScheme="blue" borderRadius="8px" px={2} fontSize="xs">
          {count}
        </Badge>
      )}
    </Flex>
  </Flex>
);

// ── Info Field ─────────────────────────────────────────────────────────────────
const InfoField = ({ label, value }) => (
  <Box>
    <Text
      fontSize="10px"
      fontWeight="700"
      color="gray.400"
      textTransform="uppercase"
      letterSpacing="0.6px"
      mb="3px"
    >
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="500" color="gray.800" wordBreak="break-word">
      {value || "N/A"}
    </Text>
  </Box>
);

// ── Section Card ───────────────────────────────────────────────────────────────
const SectionCard = ({ children }) => (
  <Card
    bg="white"
    borderRadius="14px"
    boxShadow="0 2px 8px rgba(0,0,0,0.06)"
    border="1.5px solid"
    borderColor="gray.100"
    p={6}
  >
    {children}
  </Card>
);

// ── Empty State ────────────────────────────────────────────────────────────────
const EmptyState = ({ message }) => (
  <Box
    bg="gray.50"
    borderRadius="10px"
    p={6}
    textAlign="center"
    border="1.5px dashed"
    borderColor="gray.200"
  >
    <Text fontSize="sm" color="gray.400">
      {message}
    </Text>
  </Box>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const DriverDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [url, setUrl] = useState("");

  useEffect(() => {
    get_driver_detail(location.state, setLoading).then((el) => {
      if (el.success) setData(el.reponselist);
    });
  }, [location]);

  return (
    <>
      {/* ── Top Bar ────────────────────────────────────────────────────────── */}
      <Card
        display="flex"
        flexDirection="row"
        alignItems="center"
        justify="space-between"
        paddingInline={5}
        paddingBlock={3}
        boxShadow="sm"
        borderRadius="12px"
        mb={1}
      >
        <Flex align="center" gap={3}>
          <Box
            w="36px"
            h="36px"
            borderRadius="9px"
            bg="blue.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiUser} color="blue.500" boxSize={4} />
          </Box>
          <Box>
            <Heading fontSize="lg" color="gray.800">
              Driver Details
            </Heading>
            {data?.name && (
              <Text fontSize="xs" color="gray.400">
                {data.name}
              </Text>
            )}
          </Box>
        </Flex>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title="Back"
          height="34px"
          b_color="#374151"
          onClick={() =>
            navigate("/driver-list", {
              state: {
                page: location.state?.page,
                rows: location.state?.rows,
              },
            })
          }
        />
      </Card>

      {/* ── Loading State ───────────────────────────────────────────────────── */}
      {loading ? (
        <VStack spacing={4} align="stretch" mt={4}>
          <SectionCard>
            <Flex gap={4} align="center" mb={6}>
              <SkeletonCircle size="16" />
              <Box flex={1}>
                <Skeleton h="18px" mb={2} w="45%" borderRadius="8px" />
                <Skeleton h="13px" w="30%" borderRadius="8px" />
              </Box>
            </Flex>
            <Skeleton h="12px" w="28%" mb={5} borderRadius="8px" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              {[...Array(8)].map((_, i) => (
                <Box key={i}>
                  <Skeleton h="10px" mb={2} w="40%" borderRadius="6px" />
                  <Skeleton h="14px" w="70%" borderRadius="6px" />
                </Box>
              ))}
            </SimpleGrid>
          </SectionCard>
          <SectionCard>
            <Skeleton h="14px" w="25%" mb={4} borderRadius="8px" />
            <Skeleton h="80px" borderRadius="10px" />
          </SectionCard>
        </VStack>
      ) : (
        <VStack spacing={4} align="stretch" mt={4}>

          {/* ── Profile Banner ──────────────────────────────────────────────── */}
          <SectionCard>
            <Flex align="center" gap={5} flexWrap="wrap" mb={6}>
              <Avatar
                name={data?.name}
                bg="#0175c2"
                color="white"
                size="lg"
                fontWeight="bold"
              />
              <Box flex={1} minW="160px">
                <Text fontWeight="800" fontSize="xl" color="gray.800" mb={1}>
                  {data?.name || "N/A"}
                </Text>
                <Flex gap={2} flexWrap="wrap">
                  {data?.uuid && (
                    <Badge colorScheme="orange" borderRadius="8px" px={3} py={1} fontSize="xs" fontWeight="600">
                      ID: {data.uuid}
                    </Badge>
                  )}
                  {data?.mobile1 && (
                    <Flex align="center" gap={1}>
                      <Icon as={FiPhone} color="blue.400" boxSize={3} />
                      <Text fontSize="xs" color="gray.500">{data.mobile1}</Text>
                    </Flex>
                  )}
                  {data?.email && (
                    <Flex align="center" gap={1}>
                      <Icon as={FiMail} color="green.400" boxSize={3} />
                      <Text fontSize="xs" color="gray.500">{data.email}</Text>
                    </Flex>
                  )}
                </Flex>
              </Box>
            </Flex>

            {/* Personal Info */}
            <SectionHeader icon={FiUser} title="Personal Information" color="blue.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5} mb={5}>
              <InfoField label="Full Name" value={data?.name} />
              <InfoField label="Mobile 1" value={data?.mobile1} />
              <InfoField label="Mobile 2" value={data?.mobile2} />
              <InfoField label="Email" value={data?.email} />
              <InfoField label="Address" value={data?.address} />
            </SimpleGrid>

            <Divider mb={5} />

            {/* Identity & License */}
            <SectionHeader icon={FiShield} title="Identity & License" color="purple.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              <InfoField label="License No." value={data?.license} />
              <InfoField label="License Validity" value={data?.license_validity} />
              <InfoField label="Aadhaar No." value={data?.aadhaar} />
              <InfoField label="PAN No." value={data?.pan} />
            </SimpleGrid>
          </SectionCard>

          {/* ── Documents ──────────────────────────────────────────────────── */}
          <SectionCard>
            <SectionHeader
              icon={FiFileText}
              title="Document Details"
              color="purple.500"
              count={data?.documents?.length}
            />
            {!data?.documents || data.documents.length === 0 ? (
              <EmptyState message="No documents available" />
            ) : (
              <SimpleGrid columns={[1, 2]} spacing={3}>
                {data.documents.map((el, i) => (
                  <Box
                    key={i}
                    bg="gray.50"
                    borderRadius="10px"
                    border="1.5px solid"
                    borderColor="gray.100"
                    p={4}
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <Box flex={1} mr={3}>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                          Document Name
                        </Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                          {el.name || "N/A"}
                        </Text>
                      </Box>
                      <CustomButton
                        title="View"
                        height="28px"
                        onClick={() => { setUrl(el.url); onOpen(); }}
                      />
                    </Flex>
                    <SimpleGrid columns={2} spacing={3}>
                      <InfoField label="Doc Type" value={el.doc_name} />
                      <InfoField label="Doc Number" value={el.document_number} />
                    </SimpleGrid>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </SectionCard>

          {/* ── Bank Details ────────────────────────────────────────────────── */}
          <SectionCard>
            <SectionHeader
              icon={FiCreditCard}
              title="Bank Details"
              color="teal.500"
              count={data?.banklist?.length}
            />
            {!data?.banklist || data.banklist.length === 0 ? (
              <EmptyState message="No bank accounts available" />
            ) : (
              <SimpleGrid columns={[1, 2]} spacing={3}>
                {data.banklist.map((el, i) => (
                  <Box
                    key={i}
                    bg="gray.50"
                    borderRadius="10px"
                    border="1.5px solid"
                    borderColor="gray.100"
                    p={4}
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <Box flex={1} mr={3}>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                          Account Holder
                        </Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.800">
                          {el.name || "N/A"}
                        </Text>
                      </Box>
                      {el.url && (
                        <CustomButton
                          title="View"
                          height="28px"
                          onClick={() => { setUrl(el.url); onOpen(); }}
                        />
                      )}
                    </Flex>
                    <SimpleGrid columns={2} spacing={3}>
                      <InfoField label="Bank Name" value={el.bankname} />
                      <InfoField label="Account No." value={el.account_no} />
                      <InfoField label="Branch" value={el.branch_name} />
                      <InfoField label="IFSC Code" value={el.ifsc_code} />
                    </SimpleGrid>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </SectionCard>

        </VStack>
      )}

      <ImageModal url={url} onClose={onClose} isOpen={isOpen} />
    </>
  );
};

export default DriverDetail;
